import { Field } from 'formik';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { DetailSection, LabelText } from '../../../../../components/generic';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllPaymentsQuery } from '../../../../../services/endpoints/finance/payment';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { Payment } from '../../../../../types/finance.types';
import { userTypes } from '../../../../../utils/consts';
import { paymentsColumns } from '../../../../../utils/consts/columnDefs/financeColumns';
import { paymentMethodNumbers } from '../../../../../utils/consts/finance/finance';
import {Forbidden} from "../../../../../components/error/Forbidden";
import {
  getFilterValue,
  parseCreatedAtLocationDeepLinkFilters
} from '../../../../../utils/listFilterSearchParams';
import { canSeeFinanceSummary } from '../../../../../utils/rbac';
import {normalizeCreatedAtFilterValue, toCreatedAtDateInputValue} from "../../orders/OrdersView";

const defaultScope = 'all';

// RBAC-FE-203: Global Admin excluded from Finance per policy [H]
// Note: Cannot use user?.isAdmin because isAdmin() helper includes global_admin (user_type 3)
// Must explicitly check user_type_id to exclude Global Admin
export const userCanViewEdit_Payments = (user: User | null) =>
    !!(user?.isSuper ||
        user?.isSupremeAdmin ||
        user?.user_type_id === userTypes.admin ||
        user?.isLocationAdmin);

export const PaymentsView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: defaultScope, data: { page: 1, entries: 10 } },
    { parseExtra: parseCreatedAtLocationDeepLinkFilters }
  );
  const [scopes, setScopes] = useState<Scopes>({});
  const canAccessPayments = userCanViewEdit_Payments(currentUser);
  const { data, error, isLoading, isFetching } = useGetAllPaymentsQuery(filters, {
    skip: !canAccessPayments
  });
  const [payments, setPayments] = useState<Payment[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const isGlobalUser = currentUser?.isGlobal;

  const {
    data: invoiceClients,
    error: invoiceClientsError,
    isLoading: invoiceClientsLoading
  } = useGetInvoiceClientsQuery();

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'total',
      label: 'Total',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'total')
    },
    {
      name: 'location_id',
      type: 'number',
      element: (
          <FormSelect
              name="location_id"
              label="Location"
              options={locationOptions}
              selected_Value={locationOptions?.find(
                  (loc) =>
                      String(loc.value) === getFilterValue(filters.data?.filterBy, 'location_id')
              )}
          />
      ),
    },
    {
      name: 'invoice_client',
      type: 'number',
      element: (
          <FormSelect
              name="invoice_client"
              label="Invoice Client"
              options={
                  invoiceClients?.results?.map((ic: { id: number; title: string }) => ({
                    value: ic.id,
                    label: ic.title
                  })) ?? []
              }
              loadingError={invoiceClientsError ? 'Error loading Invoice Clients' : undefined}
              selected_Value={invoiceClients?.results
                  ?.map((ic: { id: number; title: string }) => ({ value: ic.id, label: ic.title }))
                  .find(
                      (option: { value: number; label: string }) =>
                          String(option.value) ===
                          getFilterValue(filters.data?.filterBy, 'invoice_client')
                  )}
          />
      )
    },
    {
      name: 'payment_method',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'payment_method'),
      element: (
          <Form.Group controlId="payment_method">
            <Form.Label>Payment Method</Form.Label>
            <Field
                as={Form.Select}
                name="payment_method"
            >
              {Object.entries(paymentMethodNumbers).map((op) => (
                  <option
                      key={op[0]}
                      value={op[0]}
                      className="text-capitalize"
                  >
                    {op[1]}
                  </option>
              ))}
            </Field>
          </Form.Group>
      )
    },
    {
      name: 'check_number',
      label: 'Check Number',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'check_number')
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="created_at_from"
              label="Created At"
          />
      ),
      value: toCreatedAtDateInputValue(
          'greater_equal_than',
          getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
      )
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to"/>,
      value: toCreatedAtDateInputValue(
          'lesser_equal_than',
          getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
      )
    }
  ];
  function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US'); // 'en-US' locale uses MM/DD/YYYY format
  }

  if (!isGlobalUser) {
    filterFormFields.splice(4, 1);
  }

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
            data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
            data.scopes.map((sMap) =>
                Object.entries(sMap).map(([scope, title]) => {
                  let scope_count = 0;
                  if (data.scope_counts != null) {
                    scope_count = data.scope_counts[scope] ?? 0;
                  }
                  return {
                    title: `${title} (${scope_count})`,
                    key: scope
                  };
                })
            )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setPayments(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    const normalizedFilterBy = filterBy.map((filter) =>
        filter.field === 'created_at'
            ? { ...filter, value: normalizeCreatedAtFilterValue(filter.operator, filter.value) }
            : filter
    );
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: normalizedFilterBy
      }
    }));
  };

  // RBAC-FE-203: Defense-in-depth guard for finance pages
  if (!canAccessPayments) {
    return <Forbidden />;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={`${scopes[filters?.scope!] ?? ''} Payments`}
            columns={paymentsColumns}
            data={payments}
            count={data?.count}
            defaultScope={filters.scope ?? defaultScope}
            scopes={buttons}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="payments"
            sidebars={
              canSeeFinanceSummary(currentUser)
                  ? [
                    <DetailSection title="Summary">
                      <LabelText label="Total:">
                        <CurrencyCell value={data?.totals?.total ?? 0} />
                      </LabelText>
                    </DetailSection>
                  ]
                  : []
            }
            filters={filters?.data?.filterBy}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={setFilters}
        />
      </div>
  );
};

//TODO: delete this
function useGetInvoiceClientsQuery(): { data: any; error: any; isLoading: any } {
  return { data: { results: [{ id: 1, title: 'A client' }] }, error: false, isLoading: false };
}